import EventManager from '../eventManager/EventManager'
import type { Slot } from './Slot'
import {
  SLOT_RENDER_ATTR,
  DEFAULT_REFRESH_INTERVAL,
  MAX_REFRESH_RETRIES
} from '../constants'
import { AdType } from '../enums/AdType'
import type { AuctionHouse } from '../auctionHouse/AuctionHouse'

export namespace SlotRenderer {
  /**
   * Render inserts the template of a Slot instance into the DOM.
   * If the rendering did not work for any reason, it returns false.
   * Otherwise, true.
   */
  export const render = (slot: Slot): boolean => {
    if (
      !slot.validate() ||
      !slot.target ||
      !slot.insertPosition ||
      !slot.template
    ) {
      return false
    }

    slot.target.setAttribute(
      `${SLOT_RENDER_ATTR}-${slot.slotModel.adUnitId}`,
      'true'
    )
    slot.target.insertAdjacentHTML(slot.insertPosition, slot.template())
    EventManager.trigger(EventManager.events.slotWrapperRenderEnded, slot)

    return true
  }

  /**
   * This function fires when a slotRenderEnded event is emitted from googletag.
   * We can determine if ad is from a Prebid partner or an AdX partner based on
   * the event.size. If the size is [1, 1], we are dealing with a Prebid winner
   * and can skip this logic. Else, we need to handle sizing specific to AdX
   * ads here.
   */
  export const handleRenderEnded = (
    {
      size,
      isEmpty
    }: Pick<googletag.events.SlotRenderEndedEvent, 'size' | 'isEmpty'>,
    slot: Slot,
    auctionHouse: AuctionHouse
  ): void => {
    /** An 'isEmpty' event means the auctions had no winner */
    if (slot.isRefreshing) {
      if (isEmpty) {
        if (slot.refreshRetries >= MAX_REFRESH_RETRIES) {
          // In the case that we have a content slot that has refreshed, we don't want
          // to remove it from the DOM because that would cause scroll jank. So we set
          // slot.refreshTime to 0 so it stops refreshing but leaves the ad where it is.
          slot.refreshTime = 0
          slot.onRefreshRetryFail()
        } else {
          // Set the refresh time shorter so we retry the auction quickly.
          slot.refreshTime = 15
          slot.refreshRetries++
          // Need to manually trigger a slot refresh if auction was empty
          EventManager.trigger(EventManager.events.refreshSlot, slot)
        }
        return
      } else {
        // If a refresh is successful, make sure the the refresh settings are set back to normal
        slot.refreshRetries = 0
        slot.refreshTime = DEFAULT_REFRESH_INTERVAL
      }
    } else {
      if (isEmpty) {
        slot.cleanup()
        return
      }
    }

    /** We've never seen a string returned but the docs suggest its possible */
    if (typeof size === 'string') {
      return
    }

    const [adWidth, adHeight] = size as Array<number>

    // Google won the auction, so we need to size the ad wrapper here
    if (adWidth !== 1 && adHeight !== 1) {
      /** An event.size of [0, 0] represents a Google native advertisement */
      slot.adType =
        adWidth === 0 && adHeight === 0 ? AdType.adXNative : AdType.adXStandard

      slot.width = adWidth
      slot.height = adHeight
      slot.sizeContainers()
      if (slot && slot.highestPrebid) {
        auctionHouse.addBids([slot.highestPrebid])
        slot.highestPrebid = undefined
      }
    }

    if (!slot.refreshSizeRestricted && slot.restrictRefreshSize) {
      const heightRestriction =
        slot.height || slot.highestPrebid?.height || false // TODO: how do we really know what this is? injectibleCreative concerning.
      if (heightRestriction && heightRestriction > 1) {
        slot.fixedHeight = heightRestriction
        slot.sizes = slot.sizes.filter((size) => {
          if (!heightRestriction) {
            return true
          }
          return Array.isArray(size) && size[1] <= heightRestriction
        })
      }
    }

    slot.displayAdUnitLabels(slot.adType)
  }
}
